import {isEmpty, pick} from 'lodash-es';
import {useCallback, useEffect, useReducer, useState} from 'react';

import useRatesUpdate from '@/screens/Rates/Upload/useRatesUpdate';
import {tierService} from '@/services/tierService';
import type {
  AfriexCurrencyCode,
  AfriexRate,
  AfriexRatePromo,
  UserType,
} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {applyDiscountToRates} from '@/utils/rates/applyDiscountToRates';
import {convertRatesListToMap} from '@/utils/rates/convertRatesListToMap';
import {transformRatesMapToMatrix} from '@/utils/rates/transformRatesMapToMatrix';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import {TierActionType} from './types';
import updateTierReducer from './updateTierReducer';
import {extractCurrenciesFromTags} from './utils';

const useTierUpdate = (
  onClose: VoidFunction,
  initialData: Partial<AfriexRatePromo> = {},
  rawAppRates?: AfriexRate[],
  target?: UserType,
  onSuccess?: () => void | Promise<void>,
) => {
  const {
    handleArbitrageCheck,
    arbitrageResult,
    isArbitrageDetected,
    isArbitrageChecked,
    handleToleranceChange,
    tolerance,
  } = useRatesUpdate();
  const [tier, dispatch] = useReducer(
    updateTierReducer,
    initialData as AfriexRatePromo,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowArbitrageTable, setShouldShowArbitrageTable] =
    useState<boolean>(false);
  useEffect(() => {
    dispatch({
      type: TierActionType.UPDATE_TIER,
      payload: initialData,
    });
  }, []);

  const checkIfRateTierHasArbitrage = useCallback(async (): Promise<void> => {
    const appRates = rawAppRates ?? [];

    if (isEmpty(appRates) || appRates.length === 0) {
      showErrorMessage('No rates fetched. Please,refresh the page');
      return;
    }
    const rateCurrencies = extractCurrenciesFromTags(tier);
    const ratesWithDiscount = applyDiscountToRates(
      appRates ?? [],
      Math.abs(Number(tier.discount)),
      rateCurrencies,
    );

    const rates = convertRatesListToMap(ratesWithDiscount);

    const currencySymbols = Object.keys(rates) as AfriexCurrencyCode[];
    await handleArbitrageCheck(
      transformRatesMapToMatrix(rates),
      currencySymbols,
    );
    setShouldShowArbitrageTable(true);
  }, [handleArbitrageCheck, rawAppRates, tier.discount]);

  const handleSubmit = async (rawTier: AfriexRatePromo) => {
    setIsLoading(true);
    try {
      if (!rawTier.name) {
        showErrorMessage(`Please provide the name of the promo`);
        setIsLoading(false);
        return;
      }
      if (!rawTier.discount) {
        showErrorMessage(`Please provide the discount value`);
        setIsLoading(false);
        return;
      }
      if (rawTier.id) {
        const fieldsToInclude: (keyof AfriexRatePromo)[] = [
          'name',
          'target',
          'discount',
          'priority',
          'code',
          'meta',
        ];
        await tierService.updateTier(
          rawTier.id,
          pick(rawTier, fieldsToInclude),
          target,
        );
        showSuccessMessage(`Promo tier updated successfully`);
      } else {
        await tierService.createTier(rawTier, target);
        showSuccessMessage(`Promo tier saved successfully`);
      }

      onClose();

      if (onSuccess) {
        await onSuccess();
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (e) {
      showErrorMessage(`Error saving Tier ${getErrorMessage(e, String(e))}`);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    arbitrageResult,
    checkIfRateTierHasArbitrage,
    dispatch,
    handleSubmit,
    isLoading,
    isArbitrageDetected,
    isArbitrageChecked,
    handleToleranceChange,
    tolerance,
    shouldShowArbitrageTable,
    setShouldShowArbitrageTable,
    tier,
  };
};

export default useTierUpdate;
